import { DEFAULT_CHART_CONFIG } from '../config/constants';
import type { PhysicsConfig, ScalingConfig } from '../config/types';
import type { DataPoint } from '../types';

export interface PhysicsState {
    currentVal: number;
    velocity: number;
}

/**
 * Pure function to calculate the next step of the spring simulation.
 *
 * @param state - Current physics state (position and velocity)
 * @param target - Target position to move towards
 * @param dt - Delta time in seconds
 * @param time - Current timestamp (for organic noise)
 * @param config - Physics configuration (uses defaults if not provided)
 * @returns Next physics state
 */
export const calculatePhysicsStep = (
    state: PhysicsState,
    target: number,
    dt: number,
    time: number,
    config: PhysicsConfig = DEFAULT_CHART_CONFIG.physics,
    // the asset's visible half-range (priceConfig.minSpread). synthetic motion
    // is scaled to this so it reads identically on BTC (~62000) and PENGU
    // (~0.006). 0 → fall back to a fraction of the target (tests / no config).
    visibleSpread = 0,
): PhysicsState => {
    const {
        tension,
        friction,
        enableArtificialNoise,
        noiseStrength,
        organicDriftScale,
        adaptiveTension,
    } = config;

    const displacement = target - state.currentVal;
    const dist = Math.abs(displacement);

    // Non-linear spring response (Adaptive Tension)
    // For small movements, use base tension (smooth/floaty).
    // For large jumps, exponentially increase tension to "snap" to position.
    let tensionMultiplier = 1;

    if (dist > adaptiveTension.threshold) {
        // Progressive stiffening of the spring based on distance
        // E.g., with defaults: dist=5 -> multiplier ~ 6.0 -> Very Snappy
        tensionMultiplier =
            1 +
            Math.pow(
                dist - adaptiveTension.threshold,
                adaptiveTension.exponent,
            ) *
                adaptiveTension.multiplier;
    }

    // Cap the multiplier to ensure numerical stability at 60fps
    // (High tension requires smaller dt, we don't want to explode)
    const safeMultiplier = Math.min(
        tensionMultiplier,
        adaptiveTension.maxMultiplier,
    );

    const effectiveTension = tension * safeMultiplier;

    // We scale friction by sqrt(multiplier) to maintain the same Damping Ratio.
    // This prevents the cursor from oscillating wildly when tension increases.
    const effectiveFriction = friction * Math.sqrt(safeMultiplier);

    const springForce = displacement * effectiveTension;
    const dampingForce = -state.velocity * effectiveFriction;

    // Organic drift (sine waves) + Jitter (random noise)
    // Only applied if enabled in config
    let organicDrift = 0;
    let randomJitter = 0;

    if (enableArtificialNoise) {
        // scale to the visible price spread so the "breathing" is a consistent
        // fraction of the chart regardless of the asset's absolute price.
        const scale = visibleSpread > 0 ? visibleSpread : Math.abs(target) * 0.002;
        const timeS = time / 1000;
        organicDrift =
            (Math.sin(timeS * 3) + Math.cos(timeS * 7)) *
            organicDriftScale *
            scale;
        randomJitter = (Math.random() - 0.5) * noiseStrength * scale;
    }

    const acceleration =
        springForce + dampingForce + randomJitter + organicDrift;

    const newVelocity = state.velocity + acceleration * dt;
    const newValue = state.currentVal + newVelocity * dt;

    return {
        velocity: newVelocity,
        currentVal: newValue,
    };
};

/**
 * Calculates the visual Y-axis domain to keep the cursor centered.
 * Uses a symmetric expansion based on the furthest visible point.
 *
 * @param currentVal - Current cursor value
 * @param points - Array of data points in the viewport
 * @param currentMin - Current domain minimum
 * @param currentMax - Current domain maximum
 * @param config - Scaling configuration (uses defaults if not provided)
 * @param overrideMinSpread - Optional price-scaled minSpread (takes precedence over config)
 * @returns Tuple of [newMin, newMax] for the Y-axis domain
 */
export const calculateSymmetricDomain = (
    currentVal: number,
    points: DataPoint[],
    currentMin: number,
    currentMax: number,
    config: ScalingConfig = DEFAULT_CHART_CONFIG.scaling,
    overrideMinSpread?: number,
): [number, number] => {
    const { lerpFactor, padding, minSpread: configMinSpread } = config;

    // Use override if provided, otherwise use config value
    const minSpread = overrideMinSpread ?? configMinSpread;

    // If no history, use scaled minSpread for initial range
    if (points.length < 2) {
        return [currentVal - minSpread, currentVal + minSpread];
    }

    // Early-exit safe zone check: skip full recalculation if value is well within bounds
    // This saves CPU cycles when the chart is stable and price isn't near edges
    const range = currentMax - currentMin;
    const safeMargin = range * 0.2; // 20% buffer from edges

    if (
        currentVal > currentMin + safeMargin &&
        currentVal < currentMax - safeMargin
    ) {
        // Value is in the safe zone - apply minimal lerp to maintain smooth feel
        // but skip the expensive min/max scan through points
        const targetMin = currentVal - range / 2;
        const targetMax = currentVal + range / 2;

        // Use a reduced lerp factor for subtle recentering
        const gentleLerp = lerpFactor * 0.3;
        const nextMin = currentMin + (targetMin - currentMin) * gentleLerp;
        const nextMax = currentMax + (targetMax - currentMax) * gentleLerp;

        return [nextMin, nextMax];
    }

    // Full calculation when value is near edges or outside safe zone
    let dataMin = currentVal;
    let dataMax = currentVal;

    // Optimized sampling (skip every 10 points for speed)
    for (let i = 0; i < points.length; i += 10) {
        const val = points[i].value;
        if (val < dataMin) dataMin = val;
        if (val > dataMax) dataMax = val;
    }

    // Determine max distance from center
    const distToMax = Math.abs(dataMax - currentVal);
    const distToMin = Math.abs(dataMin - currentVal);
    const maxDist = Math.max(distToMax, distToMin, minSpread);

    const halfRange = maxDist * padding;
    const targetMin = currentVal - halfRange;
    const targetMax = currentVal + halfRange;

    // Lerp towards target for smooth camera movement
    const nextMin = currentMin + (targetMin - currentMin) * lerpFactor;
    const nextMax = currentMax + (targetMax - currentMax) * lerpFactor;

    return [nextMin, nextMax];
};

/**
 * Prunes the data array to remove points that have scrolled off-screen.
 *
 * @param history - Array of data points (mutated in place)
 * @param currentTime - Current timestamp
 * @param viewportMs - Viewport time window in milliseconds (uses default if not provided)
 * @param maxPoints - Optional hard cap for retained points (memory guardrail)
g */
export const pruneHistory = (
    history: DataPoint[],
    currentTime: number,
    viewportMs: number = DEFAULT_CHART_CONFIG.viewportMs,
    maxPoints?: number,
): void => {
    const cutoff = currentTime - viewportMs - 1000;

    if (history.length === 0) return;

    let low = 0;
    let high = history.length - 1;
    let firstVisible = history.length;

    while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        if (history[mid].timestamp <= cutoff) {
            low = mid + 1;
        } else {
            firstVisible = mid;
            high = mid - 1;
        }
    }

    if (firstVisible > 0) {
        history.splice(0, firstVisible);
    }

    if (maxPoints && history.length > maxPoints) {
        history.splice(0, history.length - maxPoints);
    }
};
